import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, Menu, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AddPackageForm from './AddPackageForm';
import * as projectService from '../../services/projectService';
import orderPlanService, { CreateOrderPlanItemPayload, OrderPlanItem, OrderPlanType } from '../../services/orderPlanService';
import { useNotification } from '../layouts/Layout';
import AddOrderPlanItemDialog from './AddOrderPlanItemDialog';

interface Props { projectId: string; }

const OrderPlanTab: React.FC<Props> = ({ projectId }) => {
  const { showNotification } = useNotification();
  const [items, setItems] = useState<OrderPlanItem[]>([]);
  const [loading, setLoading] = useState(false);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editInitial, setEditInitial] = useState<any | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuItemId, setMenuItemId] = useState<number | null>(null);
  const [openPackageForm, setOpenPackageForm] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const data = await orderPlanService.list(projectId);
      setItems(data);
    } catch (e: any) {
      showNotification(e?.message || 'Nie udało się pobrać planu zamówień', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [projectId]);

  const handleSubmit = async (payload: CreateOrderPlanItemPayload) => {
    try {
      if (editId) {
        await orderPlanService.update(editId, payload);
      } else {
        await orderPlanService.create(payload);
      }
      fetchData();
      showNotification(editId ? 'Pozycja zaktualizowana' : 'Pozycja dodana', 'success');
      setEditId(null);
      setEditInitial(null);
    } catch (e: any) {
      showNotification(e?.message || (editId ? 'Nie udało się zaktualizować pozycji' : 'Nie udało się dodać pozycji'), 'error');
    }
  };

  const handleMenuOpen = (e: React.MouseEvent<HTMLButtonElement>, id: number) => {
    setMenuAnchor(e.currentTarget);
    setMenuItemId(id);
  };
  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuItemId(null);
  };
  const handleDelete = async () => {
    if (!menuItemId) return;
    try {
      await orderPlanService.remove(menuItemId);
      handleMenuClose();
      fetchData();
      showNotification('Pozycja usunięta', 'success');
    } catch (e: any) {
      showNotification(e?.message || 'Nie udało się usunąć pozycji', 'error');
    }
  };
  const handleEdit = () => {
    if (!menuItemId) return;
    const item = items.find(i => i.id === menuItemId);
    if (!item) return;
    setEditInitial({
      type: item.type,
      description: item.description,
      lead_time_days: item.lead_time_days,
      deadline_date: item.deadline_date,
      status: item.status,
      order_date: item.order_date,
    });
    setEditId(item.id);
    setDialogOpen(true);
    handleMenuClose();
  };
  const handleCreatePackage = () => {
    setOpenPackageForm(true);
    handleMenuClose();
  };

  return (
    <>
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Button variant="contained" onClick={() => { setEditInitial(null); setEditId(null); setDialogOpen(true); }} disabled={loading}>Dodaj</Button>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={80}>Id</TableCell>
              <TableCell width={140}>Typ</TableCell>
              <TableCell>Opis</TableCell>
              <TableCell width={180}>Czas realizacji (dni)</TableCell>
              <TableCell width={160}>Termin (deadline)</TableCell>
              <TableCell width={160}>Termin dostawy</TableCell>
              <TableCell width={120}>Pozostało (dni)</TableCell>
              <TableCell width={180}>Status</TableCell>
              <TableCell width={160}>Termin zamówienia</TableCell>
              <TableCell width={40} align="right">Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map(item => (
              <TableRow key={item.id}>
                <TableCell>{item.sequence_number}</TableCell>
                <TableCell>{item.type === 'material' ? 'materiał' : 'usługa'}</TableCell>
                <TableCell>{item.description}</TableCell>
                <TableCell>{item.lead_time_days}</TableCell>
                <TableCell>{item.deadline_date || '-'}</TableCell>
                <TableCell>{item.delivery_date || '-'}</TableCell>
                <TableCell>
                  {(() => {
                    const remain = item.remaining_days as number | null | undefined;
                    const bg = remain === null || remain === undefined ? 'transparent'
                      : remain >= 7 ? '#E8F5E9'
                      : remain >= 1 ? '#FFF8E1'
                      : '#FFEBEE';
                    const color = remain === null || remain === undefined ? 'inherit'
                      : remain >= 7 ? '#2E7D32'
                      : remain >= 1 ? '#FF8F00'
                      : '#C62828';
                    return (
                      <Box sx={{ backgroundColor: bg, color, borderRadius: 1, px: 1, py: 0.5, display: 'inline-block', minWidth: 40, textAlign: 'center' }}>
                        {remain ?? '-'}
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell>{item.status}</TableCell>
                <TableCell>{item.order_date || '-'}</TableCell>
                <TableCell width={48} align="right">
                  <IconButton size="small" onClick={(e) => handleMenuOpen(e as any, item.id)}>
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">Brak pozycji planu zamówień.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
    <AddOrderPlanItemDialog
      open={dialogOpen}
      onClose={() => setDialogOpen(false)}
      onSubmit={handleSubmit}
      projectId={projectId}
      initial={editInitial || undefined}
    />
    <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
      <MenuItem onClick={handleEdit}>Edytuj</MenuItem>
      <MenuItem onClick={handleCreatePackage}>Utwórz pakiet</MenuItem>
      <MenuItem onClick={handleDelete}>Usuń</MenuItem>
    </Menu>
    <AddPackageForm
      open={openPackageForm}
      onClose={() => setOpenPackageForm(false)}
      onAdd={async (data, files) => {
        try {
          // potrzebujemy ID projektu – przekazane do komponentu
          const apiData = projectService['createPackage'] ? data : data; // placeholder – AddPackageForm korzysta z PackagesTab zwykle; tutaj delegujemy do serwisu bezpośrednio w pakietach mod.
          let pId = projectId;
          if (typeof pId !== 'string') pId = String(pId);
          await projectService.createPackage({ ...projectService['getPackagesByProjectId'] && { project_id: pId }, ...data, project_id: pId, files });
          setOpenPackageForm(false);
          showNotification('Pakiet utworzony', 'success');
        } catch (e: any) {
          showNotification(e?.message || 'Nie udało się utworzyć pakietu', 'error');
        }
      }}
    />
    </>
  );
};

export default OrderPlanTab;


